<template>
  <div class="bulk-paid-receipt-view">
    <!-- Actions -->
    <div class="actions mb-3">
      <b-button
        size="sm"
        variant="primary"
        :disabled="isGenerating"
        @click="generatePdf"
      >
        <span v-if="isGenerating">جاري التحميل...</span>
        <span v-else>طباعة</span>
      </b-button>
    </div>

    <!-- Printable Content -->
    <div
      ref="printContent"
      dir="rtl"
    >
      <!-- Header -->
      <div class="report-header">
        <div class="header-cell right">
          <div><strong>التاريخ</strong></div>
          <div>{{ formattedDate }}</div>
        </div>

        <div class="header-cell center">
          <h2>كشف حساب يومية الصندوق</h2>
        </div>

        <div class="header-cell left">
          <div><strong>المستخدم</strong></div>
          <div>{{ printedBy || "—" }}</div>
        </div>
      </div>

      <!-- Main Table -->
      <table class="daily-report-table">
        <thead>
          <tr>
            <th>الرقم</th>
            <th>القابض</th>
            <th>تاريخ الاستلام</th>
            <th>اسم المريض</th>
            <th>المبلغ</th>
            <th>الخدمة الطبية</th>
            <th>المنشئ</th>
            <th>تاريخ الإنشاء</th>
            <th>نوع الخدمة / التصنيف</th>
          </tr>
        </thead>

        <tbody
          v-for="(group, sectionKey) in groupedBySection"
          :key="sectionKey"
        >
          <tr
            v-for="(row, i) in group.items"
            :key="i"
            class="invoice-row"
          >
            <td>{{ row.number }}</td>
            <td>
              <div v-if="row.approvedBy">
                <div>{{ row.approvedBy.name || "-" }}</div>
              </div>
              <span v-else>-</span>
            </td>
            <td>{{ formatDate(row.approvedDate) }}</td>
            <td>{{ row.patientName }}</td>
            <td class="amount">
              {{ formatAmount(row.amount - row.discount) }}
            </td>
            <td>{{ row.itemTitle }}</td>
            <td>{{ row.createdBy || "-" }}</td>
            <td>{{ formatDate(row.createdAt) }}</td>
            <!-- Type cell shown once per group, spanning all invoices + subtotal + spacer -->
            <td
              v-if="i === 0"
              class="type-cell"
              :rowspan="group.items.length + 2"
            >
              {{ group.typeName }}
            </td>
          </tr>

          <!-- Subtotal: total directly under the amount column -->
          <tr class="subtotal-row">
            <td colspan="4">
              المجموع
            </td>
            <td class="amount">
              {{ formatAmount(group.total) }}
            </td>
            <td
              colspan="3"
              class="text-end"
            />
            <!-- <td class="subtotal-label text-center"></td> -->
          </tr>

          <!-- Spacer between groups -->
          <tr class="group-separator-row">
            <td colspan="9" />
          </tr>
        </tbody>

        <!-- Grand Total (net of refunds) -->
        <tfoot>
          <tr class="grand-total">
            <td colspan="4" />
            <td class="amount">
              {{ formatAmount(netTotal) }}
            </td>
            <td colspan="3" />
            <td class="subtotal-label">
              صافي المجموع
            </td>
          </tr>
        </tfoot>
      </table>

      <!-- Refunds -->
      <div
        v-if="refundRows.length"
        class="refund-section"
      >
        <h4>الاسترجاعات المدفوعة من صندوق القبض بموجب السندات</h4>

        <table class="daily-report-table">
          <thead>
            <tr>
              <th>رقم الوصل</th>
              <th>الوصف</th>
              <th>الوقت</th>
              <th>النظام</th>
              <th>تاريخ القبض</th>
              <th>المبلغ</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(r, i) in refundRows"
              :key="i"
            >
              <td>{{ r.invoiceNumber }}</td>
              <td>{{ r.description }}</td>
              <td>{{ r.time }}</td>
              <td>{{ r.cashier }}</td>
              <td>{{ formatDate(r.refundDate) }}</td>
              <td class="amount">
                {{ formatAmount(r.amount) }}
              </td>
            </tr>

            <tr class="subtotal-row">
              <td colspan="5">
                مجموع الاسترجاعات
              </td>
              <td class="amount">
                {{ formatAmount(totalRefunds) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Cash summary -->
      <div class="cash-summary">
        <table class="summary-table">
          <thead>
            <tr>
              <th colspan="2">
                ملخص الصندوق
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>إجمالي الفواتير</td>
              <td class="amount">
                {{ formatAmount(finalTotal) }}
              </td>
            </tr>
            <tr v-if="refundRows.length">
              <td>إجمالي الاسترجاعات</td>
              <td class="amount">
                {{ formatAmount(totalRefunds) }}
              </td>
            </tr>
            <tr>
              <td class="font-weight-bold">
                {{ refundRows.length ? "صافي الصندوق" : "إجمالي الصندوق" }}
              </td>
              <td class="amount font-weight-bold">
                {{ formatAmount(netTotal) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Footer -->
      <div class="footer-note">
        <div class="signature">
          <div class="sig-line" />
          <div>المحاسب</div>
        </div>
        <div>مطبوعة من النظام</div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'

// Arabic labels for known invoice types
const INVOICE_TYPE_LABELS = {
  invoice_out_clinic: 'عيادة خارجية',
  invoice_nicu: 'الخدج / العناية المركزة',
  invoice_patient_card_test: 'تحاليل / فحوصات',
  refund: 'استرجاع',
  invoice_insurance_surgery: 'عملية تأمين',
  invoice_emergency: 'طوارئ',
}

export default {
  name: 'BulkPaidReceipt',
  components: { BButton },

  props: {
    items: { type: Array, default: () => [] },
    refunds: { type: Array, default: () => [] },
    printedBy: { type: String, default: '' },
  },

  data() {
    return {
      isGenerating: false,
    }
  },

  computed: {
    formattedDate() {
      return new Date().toLocaleDateString('ar-IQ')
    },

    rows() {
      // Exclude refund items from the main table; they are shown in the
      // separate refunds section and used to reduce the cash total.
      return this.items
        .filter(it => it.type !== 'refund')
        .map(it => ({
          number: it.number || it.invoice_no || '-',
          groupKey:
            it.sectionGroupKey || it.type || 'other',
          sectionTitle:
            it.sectionTitle
            || INVOICE_TYPE_LABELS[it.type]
            || 'أخرى',
          patientName: it.patientName || '-',
          itemTitle: it.itemTitle || '-',
          createdAt: it.createdAt,
          approvedBy: it.approved_by || null,
          approvedDate: it.approved_at || null,
          createdBy: it.createdBy || '-',
          amount: Number(it.amount) || 0,
          discount: Number(it.discount) || 0,
          currency: it.currency || 'IQD',
        }))
    },

    groupedBySection() {
      const g = {}
      this.rows.forEach(r => {
        const key = r.groupKey
        if (!g[key]) {
          g[key] = {
            typeName: r.sectionTitle,
            items: [],
            total: 0,
          }
        }
        g[key].items.push(r)
        g[key].total += r.amount - r.discount
      })
      return g
    },

    finalTotal() {
      return Object.values(this.groupedBySection).reduce((s, g) => s + g.total, 0)
    },

    refundRows() {
      // Prefer explicit refunds prop; otherwise, derive refunds from items
      const source = (this.refunds && this.refunds.length && this.refunds)
        || this.items.filter(it => it.type === 'refund')

      return source.map(r => {
        // created_at is like "2025-12-15 04:17:56 pm"
        let time = r.time || ''
        let refundDate = r.refundDate || r.date || ''

        if (!time && typeof r.created_at === 'string') {
          const parts = r.created_at.split(' ')
          // join time + am/pm if available
          time = parts.slice(1).join(' ')
        }

        if (!refundDate && typeof r.created_at === 'string') {
          refundDate = r.created_at.split(' ')[0]
        }

        return {
          invoiceNumber: r.invoice_no || r.number || r.invoiceNumber || '-',
          description: r.description || '-',
          time,
          cashier:
            r.cashier
            || (r.created_by && r.created_by.name)
            || r.createdBy
            || this.printedBy
            || '-',
          refundDate,
          amount: Number(r.amount) || 0,
          currency: r.currency || 'IQD',
        }
      })
    },

    totalRefunds() {
      return this.refundRows.reduce((s, r) => s + (r.amount || 0), 0)
    },

    netTotal() {
      // Net cash in the drawer after subtracting refunds
      return this.finalTotal - this.totalRefunds
    },
  },

  methods: {
    formatAmount(val, currency = 'IQD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(val || 0)
    },

    formatDate(val) {
      if (!val) return '-'
      return new Date(val).toLocaleDateString('ar-IQ')
    },

    generatePdf() {
      this.isGenerating = true

      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        this.isGenerating = false
        return
      }

      const styles = `
        body {
          background: #fff;
          padding: 10px;
          font-family: Tahoma, Arial, sans-serif;
          direction: rtl;
        }

        .report-header {
          display: grid;
          grid-template-columns: 1fr 2fr 1fr;
          margin-bottom: 8px;
        }

        .header-cell {
          font-size: 10px;
        }

        .header-cell.center {
          text-align: center;
        }

        .header-cell.center h2 {
          font-size: 12px;
          margin: 0;
          font-weight: 700;
        }

        .header-cell.right {
          text-align: right;
        }

        .header-cell.left {
          text-align: left;
        }

        .daily-report-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 10px;
        }

        .daily-report-table th,
        .daily-report-table td {
          border: 1px solid #999;
          padding: 3px 4px;
          text-align: right;
        }

        .daily-report-table th {
          background: #eee;
        }

        .daily-report-table th:last-child,
        .daily-report-table td.amount {
          text-align: center;
        }

        .invoice-row td {
          border-top: 1px solid #999;
        }

        .type-cell {
          text-align: center;
          font-weight: 700;
          vertical-align: middle;
          background: #f9f9f9;
        }

        .amount {
          font-weight: 700;
        }

        .subtotal-row {
          background: #f2f2f2;
          font-weight: 700;
        }

        .grand-total {
          background: #e0e0e0;
          font-size: 11px;
          font-weight: 800;
        }

        .group-separator-row td {
          border: none;
          height: 4px;
        }

        .small-text {
          font-size: 8px;
          color: #666;
        }

        .refund-section {
          margin-top: 12px;
        }

        .refund-section h4 {
          margin-bottom: 4px;
          font-size: 11px;
        }

        .cash-summary {
          margin-top: 12px;
          display: flex;
          justify-content: center;
        }

        .summary-table {
          width: 45%;
          border-collapse: collapse;
          font-size: 10px;
        }

        .summary-table th,
        .summary-table td {
          border: 1px solid #999;
          padding: 3px 4px;
          text-align: center;
        }

        .summary-table th {
          background: #f3f4f6;
          font-weight: 700;
        }

        .footer-note {
          margin-top: 20px;
          display: flex;
          justify-content: space-between;
          font-size: 10px;
        }

        .signature {
          text-align: right;
        }

        .sig-line {
          width: 200px;
          height: 1px;
          background: #000;
          margin-bottom: 4px;
        }

        @media print {
          body {
            margin: 0;
            padding: 5mm;
          }
        }
      `

      printWindow.document.write(`
        <html dir="rtl">
          <head>
            <title>كشف حساب يومية الصندوق</title>
            <meta charset="utf-8" />
            <style>${styles}</style>
          </head>
          <body>
            ${this.$refs.printContent.innerHTML}
          </body>
        </html>
      `)

      printWindow.document.close()

      // Ensure print dialog opens after content is ready
      printWindow.onload = () => {
        printWindow.focus()
        printWindow.print()
        this.$emit('print-started')
        this.isGenerating = false
      }
    },
  },
}
</script>

<style scoped>
.bulk-paid-receipt-view {
  background: #fff;
  padding: 15px;
  font-family: Tahoma, Arial, sans-serif;
}

/* Header */
.report-header {
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  margin-bottom: 8px;
}

.header-cell {
  font-size: 10px;
}

.header-cell.center {
  text-align: center;
}

.header-cell.center h2 {
  font-size: 12px;
  margin: 0;
  font-weight: 700;
}

.header-cell.right {
  text-align: right;
}

.header-cell.left {
  text-align: left;
}

/* Table */
.daily-report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 10px;
}

.daily-report-table th,
.daily-report-table td {
  border: 1px solid #999;
  padding: 3px 4px;
  text-align: right;
}

.daily-report-table th {
  background: #eee;
}

.invoice-row td {
  border-top: 1px solid #999;
}

.type-cell {
  text-align: center;
  font-weight: 700;
  vertical-align: middle;
  background: #f9f9f9;
}

.group-header-row {
  background: #f5f5f5;
  font-weight: 700;
}

.amount {
  font-weight: 700;
  text-align: center;
}

.subtotal-label {
  text-align: right;
  font-weight: 700;
}

/* Totals */
.subtotal-row {
  background: #f2f2f2;
  font-weight: 700;
}

.grand-total {
  background: #e0e0e0;
  font-size: 11px;
  font-weight: 800;
}

/* Refunds */
.refund-section {
  margin-top: 12px;
}

.refund-section h4 {
  margin-bottom: 4px;
  font-size: 11px;
}

/* Footer */
.footer-note {
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  font-size: 10px;
}

.signature {
  text-align: right;
}

.sig-line {
  width: 200px;
  height: 1px;
  background: #000;
  margin-bottom: 4px;
}

.cash-summary {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}

.summary-table {
  width: 45%;
  border-collapse: collapse;
  font-size: 10px;
}

.summary-table th,
.summary-table td {
  border: 1px solid #999;
  padding: 3px 4px;
  text-align: center;
}

.summary-table th {
  background: #f3f4f6;
  font-weight: 700;
}

.group-separator-row td {
  border: none;
  height: 4px;
}

.small-text {
  font-size: 8px;
  color: #666;
}
</style>
